import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import './SupplementsTab.css';

export default function SupplementsTab({ clientId, client }) {
  const [supplements, setSupplements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDosage, setFormDosage] = useState('');
  const [formInstructions, setFormInstructions] = useState('');
  const [formTime, setFormTime] = useState('');

  useEffect(() => {
    loadSupplements();
  }, [clientId]);

  async function loadSupplements() {
    setLoading(true);
    const { data } = await supabase
      .from('supplements')
      .select('*')
      .eq('client_id', clientId)
      .eq('is_active', true)
      .order('sort_order')
      .order('created_at');

    setSupplements(data || []);
    setLoading(false);
  }

  function resetForm() {
    setFormName('');
    setFormDosage('');
    setFormInstructions('');
    setFormTime('');
    setShowAdd(false);
    setEditingId(null);
  }

  function startEdit(supp) {
    setEditingId(supp.id);
    setFormName(supp.name);
    setFormDosage(supp.dosage || '');
    setFormInstructions(supp.instructions || '');
    setFormTime(supp.reminder_time ? supp.reminder_time.slice(0, 5) : '');
    setShowAdd(false);
  }

  function startAdd() {
    resetForm();
    setShowAdd(true);
    setEditingId(null);
  }

  async function saveNew() {
    if (!formName.trim()) return;

    const { data } = await supabase
      .from('supplements')
      .insert({
        client_id: clientId,
        name: formName.trim(),
        dosage: formDosage.trim() || null,
        instructions: formInstructions.trim() || null,
        reminder_time: formTime || null,
        sort_order: supplements.length,
      })
      .select()
      .single();

    if (data) {
      setSupplements((prev) => [...prev, data]);
      resetForm();
    }
  }

  async function saveEdit(suppId) {
    if (!formName.trim()) return;

    await supabase
      .from('supplements')
      .update({
        name: formName.trim(),
        dosage: formDosage.trim() || null,
        instructions: formInstructions.trim() || null,
        reminder_time: formTime || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', suppId);

    setSupplements((prev) =>
      prev.map((s) =>
        s.id === suppId
          ? {
              ...s,
              name: formName.trim(),
              dosage: formDosage.trim() || null,
              instructions: formInstructions.trim() || null,
              reminder_time: formTime || null,
            }
          : s
      )
    );
    resetForm();
  }

  async function removeItem(suppId) {
    if (!window.confirm('Remove this supplement?')) return;

    await supabase
      .from('supplements')
      .update({ is_active: false })
      .eq('id', suppId);

    setSupplements((prev) => prev.filter((s) => s.id !== suppId));
  }

  function formatTime(time) {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${m} ${ampm}`;
  }

  if (loading) {
    return <div className="tab-loading">Loading supplements...</div>;
  }

  return (
    <div className="supp-tab">
      <div className="supp-tab-header">
        <div>
          <h3 className="supp-tab-title">Supplements</h3>
          <p className="supp-tab-subtitle">
            Manage {client.name.split(' ')[0]}'s supplement protocol. Set reminder times for push notifications.
          </p>
        </div>
        <button className="supp-add-top-btn" onClick={startAdd}>
          + Add
        </button>
      </div>

      {/* Supplement list */}
      {supplements.length === 0 && !showAdd && (
        <div className="supp-empty">
          <p>No supplements added yet.</p>
          <p className="supp-empty-hint">Click "+ Add" to create the first supplement.</p>
        </div>
      )}

      <div className="supp-list">
        {supplements.map((supp, idx) => (
          <div key={supp.id} className="supp-card">
            {editingId === supp.id ? (
              <div className="supp-form">
                <div className="supp-form-row">
                  <div className="form-group supp-form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. Vitamin D3"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="form-group supp-form-group">
                    <label>Dosage</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 5000 IU"
                      value={formDosage}
                      onChange={(e) => setFormDosage(e.target.value)}
                    />
                  </div>
                </div>
                <div className="supp-form-row">
                  <div className="form-group supp-form-group">
                    <label>Instructions</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. Take with food"
                      value={formInstructions}
                      onChange={(e) => setFormInstructions(e.target.value)}
                    />
                  </div>
                  <div className="form-group supp-form-group-sm">
                    <label>Reminder</label>
                    <input
                      type="time"
                      className="form-input"
                      value={formTime}
                      onChange={(e) => setFormTime(e.target.value)}
                    />
                  </div>
                </div>
                <div className="supp-form-actions">
                  <button className="btn btn-primary supp-save-btn" onClick={() => saveEdit(supp.id)} disabled={!formName.trim()}>
                    Save
                  </button>
                  <button className="btn btn-secondary supp-cancel-btn" onClick={resetForm}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="supp-card-display">
                <div className="supp-card-info">
                  <div className="supp-card-name">
                    <span className="supp-card-icon">💊</span>
                    {supp.name}
                  </div>
                  {(supp.dosage || supp.instructions) && (
                    <div className="supp-card-detail">
                      {supp.dosage && <span className="supp-dosage">{supp.dosage}</span>}
                      {supp.dosage && supp.instructions && <span> · </span>}
                      {supp.instructions && <span>{supp.instructions}</span>}
                    </div>
                  )}
                  {supp.reminder_time && (
                    <div className="supp-card-time">
                      🔔 {formatTime(supp.reminder_time)}
                    </div>
                  )}
                </div>
                <div className="supp-card-actions">
                  <button className="supp-action-btn" onClick={() => startEdit(supp)}>
                    Edit
                  </button>
                  <button className="supp-action-btn supp-action-remove" onClick={() => removeItem(supp.id)}>
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="supp-card supp-card-new">
          <div className="supp-form">
            <h4 className="supp-form-title">New Supplement</h4>
            <div className="supp-form-row">
              <div className="form-group supp-form-group">
                <label>Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Vitamin D3"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group supp-form-group">
                <label>Dosage</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. 5000 IU"
                  value={formDosage}
                  onChange={(e) => setFormDosage(e.target.value)}
                />
              </div>
            </div>
            <div className="supp-form-row">
              <div className="form-group supp-form-group">
                <label>Instructions</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Take with food"
                  value={formInstructions}
                  onChange={(e) => setFormInstructions(e.target.value)}
                />
              </div>
              <div className="form-group supp-form-group-sm">
                <label>Reminder</label>
                <input
                  type="time"
                  className="form-input"
                  value={formTime}
                  onChange={(e) => setFormTime(e.target.value)}
                />
              </div>
            </div>
            <div className="supp-form-actions">
              <button className="btn btn-primary supp-save-btn" onClick={saveNew} disabled={!formName.trim()}>
                Add Supplement
              </button>
              <button className="btn btn-secondary supp-cancel-btn" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
